<template>
  <main class="answer-page">
    <section class="answer-hero">
      <div class="answer-shell">
        <div class="answer-hero-grid">
          <div class="answer-hero-copy">
            <h1>{{ page.h1 }}</h1>
            <p class="answer-lede">{{ page.lede }}</p>
            <div class="answer-actions">
              <a
                :href="addReferralToUrl('https://app.workspace369.com/signup')"
                class="answer-primary"
                data-conversion-event="start_free_click"
                :data-conversion-label="`${page.h1} primary CTA`"
                :data-conversion-location="page.slug"
              >
                Start free trial
              </a>
              <NuxtLink
                :to="localePath('/contact-sales')"
                class="answer-secondary"
                data-conversion-event="demo_request_click"
                :data-conversion-label="`${page.h1} demo CTA`"
                :data-conversion-location="page.slug"
              >
                Request a demo
              </NuxtLink>
            </div>
            <TrialDisclosure />
          </div>

          <div class="answer-panel-wrap">
            <aside class="answer-panel">
              <div class="answer-panel-top">
                <strong>AI answer</strong>
              </div>
              <div class="answer-panel-body">
                <p>{{ page.aiAnswer }}</p>
                <dl>
                  <div v-for="fact in page.facts" :key="fact.label">
                    <dt>{{ fact.label }}</dt>
                    <dd>{{ fact.value }}</dd>
                  </div>
                </dl>
              </div>
            </aside>
          </div>
        </div>

        <!-- The real app: the Workspace369 Home (fictional workspace). Phones get a crop of its main panel so the figures
             stay readable. There is no dark capture of this Home yet, so dark mode shows the light one. -->
        <figure class="answer-stage">
          <picture class="answer-stage-light">
            <source media="(max-width: 640px)" srcset="/home/screens/home-dashboard-phone.webp" width="1030" height="967" />
            <img src="/home/screens/dashboard-light-clean.webp" :alt="shotAlt" width="1920" height="1031" />
          </picture>
          <picture class="answer-stage-dark">
            <source media="(max-width: 640px)" srcset="/home/screens/home-dashboard-phone.webp" width="1030" height="967" />
            <img src="/home/screens/dashboard-light-clean.webp" :alt="shotAlt" width="1920" height="1031" />
          </picture>
          <!-- The phone crop leaves out the sidebar's "Fictional workspace" tag, so phones label the figures here. -->
          <span class="answer-stage-tag">Fictional workspace</span>
        </figure>
      </div>
    </section>

    <section class="answer-section answer-summary-section">
      <div class="answer-shell">
        <div class="answer-section-heading">
          <h2>{{ page.summaryTitle }}</h2>
        </div>

        <div class="answer-summary-grid">
          <article v-for="item in page.summary" :key="item.title" class="answer-summary-card">
            <h3>{{ item.title }}</h3>
            <p>{{ item.copy }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="answer-band">
      <div class="answer-shell">
        <div class="answer-section-heading">
          <h2>{{ page.workflowTitle }}</h2>
        </div>

        <ol class="answer-workflow">
          <li v-for="step in page.workflow" :key="step.title">
            <h3>{{ step.title }}</h3>
            <p>{{ step.copy }}</p>
          </li>
        </ol>
      </div>
    </section>

    <section class="answer-section">
      <div class="answer-shell answer-feature-layout">
        <div class="answer-feature-copy">
          <h2>{{ page.modulesTitle }}</h2>
          <p>{{ page.modulesCopy }}</p>
        </div>

        <ul class="answer-feature-grid">
          <li v-for="module in page.modules" :key="module" class="answer-feature-chip">
            {{ module }}
          </li>
        </ul>
      </div>
    </section>

    <section v-if="page.featureAreas?.length" class="answer-section answer-areas-section">
      <div class="answer-shell">
        <div class="answer-section-heading">
          <h2>{{ page.featureAreasTitle }}</h2>
        </div>
        <p v-if="page.featureAreasCopy" class="answer-link-copy">{{ page.featureAreasCopy }}</p>
        <div class="answer-areas">
          <article v-for="area in page.featureAreas" :key="area.name" class="answer-area">
            <h3>{{ area.name }}</h3>
            <p>{{ area.copy }}</p>
            <p class="answer-area-plans">{{ area.plans }}</p>
            <NuxtLink v-if="area.path" :to="localePath(area.path)">{{ area.linkLabel }} <span aria-hidden="true">→</span></NuxtLink>
          </article>
        </div>
      </div>
    </section>

    <section v-if="page.integrationLinks?.length" class="answer-section">
      <div class="answer-shell">
        <div class="answer-section-heading">
          <h2>{{ page.integrationLinksTitle }}</h2>
        </div>
        <p v-if="page.integrationLinksCopy" class="answer-link-copy">{{ page.integrationLinksCopy }}</p>
        <ul class="answer-pricing-link answer-link-list">
          <li v-for="link in page.integrationLinks" :key="link.path">
            <NuxtLink :to="localePath(link.path)">{{ link.label }}</NuxtLink>
          </li>
        </ul>
      </div>
    </section>

    <section v-if="page.showPricing" class="answer-pricing-section">
      <div class="answer-shell">
        <div class="answer-section-heading">
          <h2>Simple tiers for different stages of the business.</h2>
        </div>

        <div class="answer-pricing-grid">
          <article v-for="plan in pricingPlans" :key="plan.slug" class="answer-price-card">
            <span>{{ plan.label }}</span>
            <h3>{{ plan.name }}</h3>
            <p class="answer-price">${{ plan.monthly }}<small>/mo</small></p>
            <p>${{ plan.yearlyMonthly }}/mo billed yearly</p>
          </article>
        </div>
        <p v-if="page.pricingLink" class="answer-pricing-link">
          <NuxtLink :to="localePath(page.pricingLink.path)">{{ page.pricingLink.label }} <span aria-hidden="true">→</span></NuxtLink>
        </p>
      </div>
    </section>

    <section class="answer-section answer-faq-section">
      <div class="answer-shell answer-faq-layout">
        <div class="answer-section-heading">
          <h2>{{ page.faqTitle }}</h2>
        </div>

        <div class="answer-faq-grid">
          <article v-for="item in page.faq" :key="item.question">
            <h3>{{ item.question }}</h3>
            <p>{{ item.answer }}</p>
          </article>
        </div>
      </div>
    </section>

    <Cta03 />
    <PhoneStickyCta label="Answer page" :location="$route.path" watch=".answer-hero .answer-actions" />
  </main>
</template>
<script setup lang="ts">
import { localizedUrl } from '~/data/locales';
import { pricingPlans } from '~/data/pricing-plans';

const props = defineProps<{
  page: {
    slug: string;
    metaTitle: string;
    description: string;
    eyebrow: string;
    h1: string;
    lede: string;
    aiAnswer: string;
    facts: Array<{ label: string; value: string }>;
    summaryKicker: string;
    summaryTitle: string;
    summary: Array<{ kicker: string; title: string; copy: string }>;
    workflowKicker: string;
    workflowTitle: string;
    workflow: Array<{ title: string; copy: string }>;
    modulesKicker: string;
    modulesTitle: string;
    modulesCopy: string;
    modules: string[];
    featureAreasKicker?: string;
    featureAreasTitle?: string;
    featureAreasCopy?: string;
    featureAreas?: Array<{ name: string; copy: string; plans: string; path?: string; linkLabel?: string }>;
    showPricing?: boolean;
    pricingLink?: { path: string; label: string };
    faqTitle: string;
    faq: Array<{ question: string; answer: string }>;
    integrationLinksKicker?: string;
    integrationLinksTitle?: string;
    integrationLinksCopy?: string;
    integrationLinks?: Array<{ path: string; label: string }>;
  };
}>();

const { addReferralToUrl } = useReferral();
const { canonicalLocaleCode, localePath } = useWorkspaceLocale();
const siteUrl = 'https://workspace369.com';
const canonicalUrl = computed(() => localizedUrl(siteUrl, props.page.slug, canonicalLocaleCode.value));

const shotAlt = 'Workspace369 Home: overdue invoices, proposals waiting on a reply, and revenue, expenses and profit for the year so far';

useHead(() => {
  return {
    title: props.page.metaTitle,
    meta: [
      { name: 'description', content: props.page.description },
      { name: 'twitter:title', content: props.page.metaTitle },
      { name: 'twitter:description', content: props.page.description },
      { name: 'twitter:card', content: 'summary_large_image' },
      { property: 'og:title', content: props.page.metaTitle },
      { property: 'og:description', content: props.page.description },
      { property: 'og:url', content: canonicalUrl.value },
    ],
    link: [{ key: 'canonical', rel: 'canonical', href: canonicalUrl.value }],
    script: [
      {
        key: `answer-page-schema-${props.page.slug}`,
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'BreadcrumbList',
              '@id': `${canonicalUrl.value}#breadcrumbs`,
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
                { '@type': 'ListItem', position: 2, name: props.page.h1, item: canonicalUrl.value },
              ],
            },
            {
              '@type': 'FAQPage',
              '@id': `${canonicalUrl.value}#faq`,
              mainEntity: props.page.faq.map((item) => ({
                '@type': 'Question',
                name: item.question,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: item.answer,
                },
              })),
            },
          ],
        }),
      },
    ],
  };
});
</script>

<style scoped>
/*
  Answer pages, in the landing palette only (components/Hero.vue): #ffffff → #f9fdff → #eefaff with the
  rgb(27 167 206) glow in light, #020617 → #07111f → #0f172a in dark, cyan #1ba7ce, teal #0e7490, deep teal
  #155e75 and #67e8f9. Tide: a full-bleed teal hero, the real app on it, and the answer card across its lower edge.
  Hero order everywhere: the answer copy, then the real app, then the AI answer card with its facts.
*/
.answer-page {
  --ink: #0f172a;
  --ink2: #475569;
  --ink3: #64748b;
  --edge: #e2e8f0;
  --page: #ffffff;
  --card: #ffffff;
  --accent: #0e7490;
  --cta-bg: #0e7490;
  --cta-fg: #ffffff;
  --cta2-edge: #cbd5e1;
  --cta2-fg: #0f172a;
  --hero-bg: linear-gradient(180deg, #ffffff 0%, #f9fdff 42%, #eefaff 100%);
  --hero-ink: #0f172a;
  --hero-ink2: #475569;
  --hero-edge: #cbd5e1;
  --panel-bg: #ffffff;
  --panel-ink: #0f172a;
  --panel-ink3: #64748b;
  --panel-edge: #e2e8f0;
  --band-bg: #020617;
  --band-ink: #ffffff;
  --band-ink2: #cbd5e1;
  --band-accent: #67e8f9;
  --band-edge: rgb(148 163 184 / 0.3);
  --alt-bg: linear-gradient(180deg, #f9fdff 0%, #eefaff 100%);
  --shadow: 0 40px 90px -44px rgb(15 23 42 / 0.35);
  --stage-shadow: 0 40px 90px -44px rgb(14 116 144 / 0.45);
  background: var(--page);
  color: var(--ink);
}

.dark .answer-page {
  --ink: #f8fafc;
  --ink2: #cbd5e1;
  --ink3: #94a3b8;
  --edge: #1e293b;
  --page: #020617;
  --card: #0b1220;
  --accent: #67e8f9;
  --cta-bg: #1ba7ce;
  --cta-fg: #020617;
  --cta2-edge: #334155;
  --cta2-fg: #f8fafc;
  --hero-bg:
    radial-gradient(50rem 30rem at 85% -5%, rgb(27 167 206 / 0.22), transparent 70%),
    linear-gradient(180deg, #020617 0%, #07111f 48%, #0f172a 100%);
  --hero-ink: #f8fafc;
  --hero-ink2: #cbd5e1;
  --hero-edge: #1e293b;
  --panel-bg: #0b1220;
  --panel-ink: #f8fafc;
  --panel-ink3: #94a3b8;
  --panel-edge: #1e293b;
  --band-bg: #07111f;
  --alt-bg: linear-gradient(180deg, #020617 0%, #0b1220 100%);
  --shadow: none;
  --stage-shadow: 0 40px 100px -44px rgb(27 167 206 / 0.4);
}

.answer-shell {
  width: min(100% - 2rem, 76rem);
  margin: 0 auto;
}

/* ---------- Hero ---------- */
.answer-hero {
  overflow: clip;
  background: var(--hero-bg);
  color: var(--hero-ink);
  padding: 8rem 0 4rem;
}

.answer-hero > .answer-shell {
  display: grid;
  grid-template-areas: "copy" "stage" "panel";
  gap: 2.5rem;
}

.answer-hero-grid {
  display: contents;
}

.answer-hero-copy {
  grid-area: copy;
  min-width: 0;
}

.answer-stage {
  grid-area: stage;
}

.answer-panel-wrap {
  grid-area: panel;
  min-width: 0;
}

.answer-hero h1 {
  margin: 0;
  color: var(--hero-ink);
  font-size: clamp(2.15rem, 5vw, 4.4rem);
  font-weight: 800;
  letter-spacing: -0.025em;
  line-height: 1.02;
  text-wrap: balance;
}

.answer-lede {
  max-width: 40rem;
  margin-top: 1.2rem;
  color: var(--hero-ink2);
  font-size: 1.06rem;
  line-height: 1.72;
}

.answer-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1.9rem;
}

.answer-primary,
.answer-secondary {
  display: inline-flex;
  min-height: 3rem;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  padding: 0.8rem 1.35rem;
  font-weight: 700;
}

.answer-primary {
  background: var(--cta-bg);
  color: var(--cta-fg);
}

.answer-secondary {
  border: 1px solid var(--cta2-edge);
  color: var(--cta2-fg);
}

.answer-hero-copy :deep(.trial-disclosure) {
  color: var(--hero-ink2);
}

.answer-stage {
  margin: 0;
  overflow: hidden;
  border: 1px solid var(--hero-edge);
  border-radius: 10px;
  background: #ffffff;
  box-shadow: var(--stage-shadow);
  aspect-ratio: 1920 / 1031;
}

.answer-stage-tag {
  display: none;
}

.answer-stage img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top left;
}

.answer-stage-dark,
.dark .answer-stage-light {
  display: none;
}

.dark .answer-stage-dark {
  display: block;
}

.dark .answer-stage {
  padding: 6px;
  background: #0f172a;
}

.dark .answer-stage img {
  border-radius: 6px;
}

.answer-panel {
  overflow: hidden;
  border: 1px solid var(--panel-edge);
  border-radius: 10px;
  background: var(--panel-bg);
  box-shadow: var(--shadow);
  color: var(--panel-ink);
}

.answer-panel-top {
  border-bottom: 1px solid var(--panel-edge);
  padding: 0.85rem 1.35rem;
}

.answer-panel-top strong {
  color: var(--panel-ink3);
  font-size: 0.8rem;
  font-weight: 700;
}

.answer-panel-body {
  display: grid;
  gap: 1.25rem;
  padding: 1.35rem;
}

.answer-panel-body p {
  color: var(--panel-ink);
  font-size: 1.04rem;
  font-weight: 600;
  line-height: 1.6;
}

.answer-panel-body dl {
  display: grid;
  gap: 0.75rem;
  margin: 0;
}

.answer-panel-body div {
  display: grid;
  gap: 0.2rem;
  border-top: 1px solid var(--panel-edge);
  padding-top: 0.75rem;
}

.answer-panel-body dt {
  color: var(--panel-ink3);
  font-size: 0.78rem;
  font-weight: 600;
}

.answer-panel-body dd {
  margin: 0;
  color: var(--panel-ink);
  font-size: 0.92rem;
  font-weight: 600;
}

/* ---------- Sections ---------- */
.answer-section,
.answer-pricing-section {
  padding: 5rem 0;
}

.answer-section-heading {
  max-width: 46rem;
}

.answer-section h2,
.answer-pricing-section h2,
.answer-band h2,
.answer-feature-copy h2 {
  margin: 0;
  color: var(--ink);
  font-size: clamp(1.8rem, 3.4vw, 2.75rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.1;
  text-wrap: balance;
}

.answer-summary-grid,
.answer-pricing-grid,
.answer-faq-grid,
.answer-areas {
  display: grid;
  gap: 1rem;
  margin-top: 2.25rem;
}

.answer-summary-card,
.answer-area,
.answer-faq-grid article {
  border-top: 1px solid var(--edge);
  padding-top: 1.1rem;
}

.answer-summary-card h3,
.answer-area h3,
.answer-faq-grid h3,
.answer-price-card h3 {
  margin: 0;
  color: var(--ink);
  font-size: 1.08rem;
  font-weight: 700;
  line-height: 1.32;
}

.answer-summary-card p,
.answer-area p,
.answer-faq-grid p,
.answer-feature-copy p,
.answer-link-copy {
  margin-top: 0.55rem;
  color: var(--ink2);
  line-height: 1.66;
}

.answer-link-copy {
  max-width: 48rem;
}

.answer-band {
  background: var(--band-bg);
  color: var(--band-ink);
  padding: 5rem 0;
}

.answer-band h2 {
  color: var(--band-ink);
}

.answer-workflow {
  display: grid;
  gap: 1.75rem;
  margin: 2.5rem 0 0;
  padding: 0;
  list-style: none;
}

/* Each step's rule starts with a short accent stroke; the list order carries the sequence, no numerals. */
.answer-workflow li {
  border-top: 1px solid var(--band-edge);
  background: linear-gradient(var(--band-accent), var(--band-accent)) 0 -1px / 2.5rem 3px no-repeat;
  padding-top: 1.1rem;
}

.answer-workflow h3 {
  margin-top: 0.3rem;
  color: var(--band-ink);
  font-size: 1.15rem;
  font-weight: 700;
}

.answer-workflow p {
  margin-top: 0.4rem;
  color: var(--band-ink2);
  line-height: 1.6;
}

.answer-feature-layout {
  display: grid;
  gap: 2rem;
  align-items: start;
}

.answer-feature-copy p {
  max-width: 34rem;
}

.answer-feature-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.answer-feature-chip {
  border: 1px solid var(--edge);
  border-radius: 6px;
  background: var(--card);
  padding: 0.45rem 0.7rem;
  color: var(--ink);
  font-size: 0.88rem;
  font-weight: 500;
}

.answer-areas-section {
  border-top: 1px solid var(--edge);
}

.answer-area .answer-area-plans {
  color: var(--ink);
  font-size: 0.86rem;
  font-weight: 600;
  line-height: 1.5;
}

.answer-area a,
.answer-pricing-link a,
.answer-link-list a {
  color: var(--accent);
  font-weight: 700;
}

.answer-area a {
  display: inline-block;
  margin-top: 0.6rem;
  font-size: 0.92rem;
}

.answer-area a:hover,
.answer-pricing-link a:hover,
.answer-link-list a:hover {
  text-decoration: underline;
}

.answer-pricing-link {
  margin-top: 1.6rem;
}

.answer-link-list {
  display: grid;
  gap: 0.5rem;
  margin: 1.25rem 0 0;
  padding: 0;
  list-style: none;
}

.answer-pricing-section {
  background: var(--alt-bg);
}

.answer-price-card {
  border: 1px solid var(--edge);
  border-radius: 10px;
  background: var(--card);
  padding: 1.1rem;
}

.answer-price-card span {
  display: inline-block;
  border: 1px solid var(--edge);
  border-radius: 4px;
  padding: 0.1rem 0.4rem;
  color: var(--ink2);
  font-size: 0.74rem;
  font-weight: 600;
}

.answer-price-card h3 {
  margin-top: 0.7rem;
}

.answer-price {
  margin-top: 0.4rem;
  color: var(--ink);
  font-size: 2rem;
  font-weight: 800;
  line-height: 1.1;
}

.answer-price small,
.answer-price-card p:last-child {
  color: var(--ink2);
  font-size: 0.86rem;
  font-weight: 500;
}

.answer-faq-layout {
  display: grid;
  gap: 1rem;
}

/* =========== Tide: a full-bleed teal hero; the answer card sits across its lower edge =========== */
html:root .answer-page {
  --hero-bg:
    radial-gradient(46rem 30rem at 92% 42%, rgb(27 167 206 / 0.75), transparent 68%),
    radial-gradient(30rem 20rem at 100% 0%, rgb(103 232 249 / 0.3), transparent 70%),
    radial-gradient(44rem 34rem at -5% 105%, rgb(21 94 117 / 0.95), transparent 70%),
    linear-gradient(160deg, #155e75 0%, #0e7490 55%, #155e75 100%);
  --hero-ink: #ffffff;
  --hero-ink2: rgb(255 255 255 / 0.9);
  --hero-edge: rgb(255 255 255 / 0.35);
  --cta-bg: #ffffff;
  --cta-fg: #155e75;
  --cta2-edge: rgb(255 255 255 / 0.6);
  --cta2-fg: #ffffff;
  --band-bg:
    radial-gradient(40rem 22rem at 100% 100%, rgb(103 232 249 / 0.22), transparent 70%),
    linear-gradient(115deg, #155e75 0%, #0e7490 100%);
  --band-ink2: #ffffff;
  --band-accent: #ffffff;
  --band-edge: rgb(255 255 255 / 0.3);
  --shadow: 0 40px 90px -40px rgb(2 6 23 / 0.45);
  --stage-shadow: 0 40px 90px -36px rgb(2 6 23 / 0.6);
}

html.dark:root .answer-page {
  --hero-bg:
    radial-gradient(46rem 30rem at 92% 42%, rgb(27 167 206 / 0.45), transparent 68%),
    radial-gradient(30rem 20rem at 100% 0%, rgb(103 232 249 / 0.16), transparent 70%),
    linear-gradient(115deg, #07111f 0%, #07111f 20%, #155e75 75%, #155e75 100%);
  --hero-ink2: #cbd5e1;
  --cta-bg: #1ba7ce;
  --cta-fg: #020617;
  --band-bg: linear-gradient(115deg, #07111f 0%, #155e75 100%);
  --band-ink2: #cbd5e1;
  --band-accent: #67e8f9;
  --shadow: none;
}

/* The teal stops 9rem above the hero's end, so the answer card straddles the edge into the page. */
html:root .answer-hero {
  background: var(--hero-bg) top / 100% calc(100% - 9rem) no-repeat, var(--page);
  padding-bottom: 0;
}

html:root .answer-hero-copy :deep(.trial-disclosure) {
  color: var(--hero-ink2);
}

/* The app sits in a navy frame so it reads as a device on the teal, not a grey slab. */
html:root .answer-stage {
  padding: 6px;
  border-color: rgb(255 255 255 / 0.18);
  background: #0f172a;
}

html:root .answer-stage img {
  border-radius: 6px;
}

html:root .answer-page {
  --stage-shadow:
    0 0 0 1px rgb(103 232 249 / 0.55),
    0 0 40px 2px rgb(103 232 249 / 0.45),
    0 0 160px 40px rgb(27 167 206 / 0.55),
    0 60px 120px -30px rgb(2 6 23 / 0.75);
}

/* ---------- Breakpoints ---------- */
@media (min-width: 720px) {
  .answer-hero {
    padding-top: 9.5rem;
  }

  .answer-summary-grid,
  .answer-faq-grid,
  .answer-areas {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 2rem 3rem;
  }

  .answer-pricing-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .answer-workflow {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 2rem;
  }
}

@media (min-width: 980px) {
  /* Copy beside the app; the answer card runs full width below, answer beside its facts. */
  .answer-hero > .answer-shell {
    grid-template-columns: minmax(0, 0.92fr) minmax(0, 1.08fr);
    grid-template-areas: "copy stage" "panel panel";
    column-gap: 3.5rem;
    row-gap: 3.5rem;
    align-items: center;
  }

  .answer-panel-body {
    grid-template-columns: minmax(0, 1.55fr) minmax(0, 1fr);
    gap: 2.5rem;
    padding: 1.6rem;
  }

  /* Tide: the app takes ~58% of the hero, tilted slightly toward the copy, and the card crosses the teal's edge. */
  html:root .answer-hero > .answer-shell {
    grid-template-columns: minmax(0, 0.72fr) minmax(0, 1.28fr);
    column-gap: 3rem;
    row-gap: 4.5rem;
  }

  /* Sized so "Workspace369?" never leaves its "?" on a line of its own. */
  html:root .answer-hero h1 {
    font-size: clamp(2.4rem, 3.6vw, 3.3rem);
  }

  html:root .answer-stage {
    transform: perspective(2200px) rotateY(-7deg) rotateX(2deg);
    transform-origin: left center;
  }

  .answer-feature-layout {
    grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
    gap: 3.5rem;
  }

  .answer-pricing-grid {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }

  .answer-areas {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .answer-faq-layout {
    grid-template-columns: minmax(0, 20rem) minmax(0, 1fr);
    gap: 3rem;
  }

  .answer-faq-layout .answer-section-heading {
    position: sticky;
    top: 9rem;
    align-self: start;
  }

  .answer-faq-layout h2 {
    font-size: 2rem;
  }

  .answer-faq-grid {
    margin-top: 0;
  }
}

/* Below desktop the hero reads question, trial button and its line, then the app, then the lede and answer card. */
@media (max-width: 979px) {
  html:root .answer-hero > .answer-shell {
    grid-template-areas: "h1" "actions" "note" "stage" "lede" "panel";
    row-gap: 0;
  }

  html:root .answer-hero-copy { display: contents; }
  html:root .answer-hero h1 { grid-area: h1; }
  html:root .answer-actions { grid-area: actions; margin-top: 1.4rem; }
  html:root .answer-hero-copy :deep(.trial-disclosure) { grid-area: note; }
  html:root .answer-stage { grid-area: stage; margin-top: 2rem; }
  html:root .answer-lede { grid-area: lede; margin-top: 2rem; }
  html:root .answer-panel-wrap { grid-area: panel; margin-top: 2.5rem; }
}

@media (max-width: 640px) {
  .answer-hero h1 {
    font-size: 2.15rem;
  }

  .answer-actions > * {
    flex: 1 1 100%;
  }

  .answer-stage {
    aspect-ratio: 1030 / 967;
    position: relative;
  }

  .answer-stage-tag {
    display: block;
    position: absolute;
    right: 12px;
    bottom: 12px;
    padding: 0.2rem 0.5rem;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    background: rgb(255 255 255 / 0.94);
    color: #4b5563;
    font-size: 0.72rem;
    font-weight: 600;
    line-height: 1.3;
  }

  .answer-section,
  .answer-pricing-section,
  .answer-band {
    padding: 3.5rem 0;
  }

  html:root .answer-hero {
    background-size: 100% calc(100% - 14rem), auto;
  }

  /* The buttons stack here, which would put the demo button between the trial button and its terms.
     Unwrap the row so the terms sit directly under the trial button (legal B1.1). */
  html:root .answer-hero > .answer-shell {
    grid-template-areas: "h1" "actions" "note" "demo" "stage" "lede" "panel";
  }

  html:root .answer-actions { display: contents; }
  html:root .answer-primary { grid-area: actions; margin-top: 1.4rem; }
  html:root .answer-secondary { grid-area: demo; margin-top: 0.75rem; }
}
</style>
